import {useCallback, useEffect, useMemo, useRef} from 'react';
import {renderToString} from 'react-dom/server';
import styled from 'styled-components';
import {Typography} from '@/components/Typography';
import {DeliveryTileData} from '@/map/@types';
import {MAP_LAYER_Z_INDEX} from '@/map/constants';
import {Polygon} from '@/map/modules';
import {convertToCoordFromPointCoords} from '@/map/utils';
import {palette} from '@/styles';

interface IProps {
  map: naver.maps.Map;
  item: DeliveryTileData & {opacity: number};
}

export const DeliveryBlock = ({map, item}: IProps) => {
  const evt = useRef<any>();
  const info = useRef<any>();

  const paths = useMemo<naver.maps.PolygonOptions['paths'] | undefined>(() => {
    if (item?.geometry?.coordinates?.length) {
      return [
        item.geometry.coordinates[0].map((i) => {
          return convertToCoordFromPointCoords(i) as naver.maps.Coord;
        }),
      ];
    }
  }, [item?.geometry]);

  const onClick = useCallback(
    (e: {coord: naver.maps.LatLng}) => {
      if (!info.current) {
        info.current = new naver.maps.InfoWindow({
          content: renderToString(
            <Info>
              <Typography as="div" variant="medium10">
                선택 지역 내 주문 비중
              </Typography>
              <Typography as="div" variant="medium12">
                {Math.round(item.percent * 100) / 100}%
                {!!item?.totalTransactionCount && (
                  <span>&nbsp;({item?.totalTransactionCount})</span>
                )}
              </Typography>
            </Info>
          ),
          disableAnchor: true,
          backgroundColor: 'transparent',
          borderWidth: 0,
        });
      }

      info.current.open(map, e.coord);
    },
    [map, item]
  );

  const clearInfoPoint = useCallback(() => info.current?.close?.(), []);

  useEffect(() => {
    if (map) {
      evt.current = map.addListener('click', clearInfoPoint);
    }

    return () => {
      if (info.current) {
        info.current.close();
        info.current.setMap(null);
      }
      if (evt.current) {
        map.removeListener(evt.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  if (!item || !paths || item.opacity < 0.15) {
    return null;
  }

  return (
    <Polygon
      map={map}
      paths={paths}
      clickable={true}
      fillColor="#0056ff"
      fillOpacity={item.opacity}
      strokeWeight={0}
      zIndex={MAP_LAYER_Z_INDEX.DATA_TILE}
      onClick={onClick}
    />
  );
};

const Info = styled.div`
  padding: 8px;
  border-radius: 5px;
  box-shadow: 0 2px 4px 0 ${palette.shadow01};
  background-color: ${palette.black};
  color: ${palette.white};
`;
